// PumpConfigModal.jsx - Pump configuration (unified premium layout)
import React, { useState } from 'react';
import { X, Zap, Settings, Monitor } from 'lucide-react';
import SearchableTagSelect from './SearchableTagSelect';

const DEFAULT_PUMP_CONFIG = {
  onFeedback:    { tagName: '', color: '#00ff00', label: 'Running' },
  offFeedback:   { tagName: '', color: '#ff0000', label: 'Stopped' },
  trip:          { tagName: '', color: '#ffff00', label: 'Trip' },
  faultColor:    '#f59e0b',
  intermediateColor: '#fb923c',
  onCommand:     { tagName: '', color: '#3b82f6' },
  offCommand:    { tagName: '', color: '#64748b' },
  mode:          { tagName: '' },
  remoteLocal:   { tagName: '', remoteColor: '#6366f1', localColor: '#f59e0b' }
};

const THEME = (dark) => dark ? {
  bg: '#0f1117', card: '#161922', text: '#e5e7eb', sub: '#7a8599',
  border: '#2a2f3d', accent: '#667eea', success: '#10b981',
} : {
  bg: '#ffffff', card: '#f9fafb', text: '#111827', sub: '#6b7280',
  border: '#e5e7eb', accent: '#667eea', success: '#10b981',
};

function TagRow({ label, tagName, color, onTagChange, onColorChange, availableTags, darkMode, t, showColor = true }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: t.sub, marginBottom: '4px' }}>{label}</div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <SearchableTagSelect value={tagName} onChange={onTagChange} tags={availableTags} placeholder="Select tag…" darkMode={darkMode} />
        </div>
        {showColor && color !== undefined && (
          <div style={{ position: 'relative', width: '28px', height: '28px', flexShrink: 0 }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: color, border: '2px solid rgba(255,255,255,0.1)', boxShadow: `0 0 8px ${color}44` }} />
            <input type="color" value={color} onChange={e => onColorChange(e.target.value)}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
          </div>
        )}
      </div>
    </div>
  );
}

function ColorCircle({ color, onChange, label, t }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginRight: '12px' }}>
      <div style={{ position: 'relative', width: '22px', height: '22px' }}>
        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: color, border: '2px solid rgba(255,255,255,0.1)', boxShadow: `0 0 6px ${color}44` }} />
        <input type="color" value={color} onChange={e => onChange(e.target.value)}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
      </div>
      <span style={{ fontSize: '10px', color: t.sub }}>{label}</span>
    </div>
  );
}

function Toggle({ value, onChange, label, t }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
      <div onClick={() => onChange(!value)} style={{
        width: '32px', height: '17px', borderRadius: '9px', position: 'relative', cursor: 'pointer',
        background: value ? t.accent : t.border, transition: 'background .2s',
      }}>
        <div style={{ position: 'absolute', top: '2px', left: value ? '17px' : '2px', width: '13px', height: '13px',
          borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
      </div>
      <span style={{ fontSize: '11px', color: t.sub }}>{label}</span>
    </label>
  );
}

function SectionHead({ icon, title, t }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${t.border}` }}>
      {icon}
      <span style={{ fontSize: '11px', fontWeight: '700', color: t.text, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
    </div>
  );
}

export default function PumpConfigModal({ component, availableTags, onSave, onClose, darkMode }) {
  const existing = component?.pumpConfig || {};
  const merge = (key) => ({ ...DEFAULT_PUMP_CONFIG[key], ...(existing[key] || {}) });
  const t = THEME(darkMode);

  const [label, setLabel]               = useState(component?.label || '');
  const [defaultColor, setDefaultColor] = useState(component?.defaultColor || '#64748b');
  const [displayTrend, setDisplayTrend] = useState(component?.displayTrend || false);
  const [showFaceplate, setShowFaceplate] = useState(component?.showFaceplate || false);
  const [showStatusLabel, setShowStatusLabel] = useState(component?.showStatusLabel !== false);

  const [onFeedback,    setOnFeedback]    = useState(merge('onFeedback'));
  const [offFeedback,   setOffFeedback]   = useState(merge('offFeedback'));
  const [trip,          setTrip]          = useState(merge('trip'));
  const [faultColor,    setFaultColor]    = useState(existing.faultColor    ?? DEFAULT_PUMP_CONFIG.faultColor);
  const [interColor,    setInterColor]    = useState(existing.intermediateColor ?? DEFAULT_PUMP_CONFIG.intermediateColor);
  const [onCommand,     setOnCommand]     = useState(merge('onCommand'));
  const [offCommand,    setOffCommand]    = useState(merge('offCommand'));
  const [mode,          setMode]          = useState(merge('mode'));
  const [remoteLocal,   setRemoteLocal]   = useState(merge('remoteLocal'));

  const handleSave = () => {
    onSave({
      ...component, label, defaultColor, displayTrend, showFaceplate, showStatusLabel,
      pumpConfig: { onFeedback, offFeedback, trip, faultColor, intermediateColor: interColor, onCommand, offCommand, mode, remoteLocal }
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
      <div style={{ background: t.bg, borderRadius: '14px', width: '100%', maxWidth: '780px', maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: `0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px ${t.border}`, border: `1px solid ${t.border}` }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: `${t.accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={16} color={t.accent} />
            </div>
            <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>Configure Pump</span>
          </div>
          <button onClick={onClose} style={{ width: '28px', height: '28px', borderRadius: '8px', border: `1px solid ${t.border}`, background: 'rgba(255,255,255,0.05)', color: t.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr 0.7fr', gap: '14px', marginBottom: '14px' }}>

            {/* Commands */}
            <div style={{ background: t.card, borderRadius: '10px', padding: '14px', border: `1px solid ${t.border}` }}>
              <SectionHead icon={<Settings size={12} color={t.accent} />} title="Commands" t={t} />
              <TagRow label="On Command" tagName={onCommand.tagName} color={onCommand.color}
                onTagChange={v => setOnCommand(p => ({ ...p, tagName: v }))} onColorChange={v => setOnCommand(p => ({ ...p, color: v }))}
                availableTags={availableTags} darkMode={darkMode} t={t} />
              <TagRow label="Off Command" tagName={offCommand.tagName} color={offCommand.color}
                onTagChange={v => setOffCommand(p => ({ ...p, tagName: v }))} onColorChange={v => setOffCommand(p => ({ ...p, color: v }))}
                availableTags={availableTags} darkMode={darkMode} t={t} />
              <TagRow label="Mode" tagName={mode.tagName}
                onTagChange={v => setMode(p => ({ ...p, tagName: v }))}
                availableTags={availableTags} darkMode={darkMode} t={t} showColor={false} />
            </div>

            {/* Feedback */}
            <div style={{ background: t.card, borderRadius: '10px', padding: '14px', border: `1px solid ${t.border}` }}>
              <SectionHead icon={<Zap size={12} color={t.success} />} title="Feedback" t={t} />
              <TagRow label="On Feedback" tagName={onFeedback.tagName} color={onFeedback.color}
                onTagChange={v => setOnFeedback(p => ({ ...p, tagName: v }))} onColorChange={v => setOnFeedback(p => ({ ...p, color: v }))}
                availableTags={availableTags} darkMode={darkMode} t={t} />
              <TagRow label="Off Feedback" tagName={offFeedback.tagName} color={offFeedback.color}
                onTagChange={v => setOffFeedback(p => ({ ...p, tagName: v }))} onColorChange={v => setOffFeedback(p => ({ ...p, color: v }))}
                availableTags={availableTags} darkMode={darkMode} t={t} />
              <TagRow label="Trip" tagName={trip.tagName} color={trip.color}
                onTagChange={v => setTrip(p => ({ ...p, tagName: v }))} onColorChange={v => setTrip(p => ({ ...p, color: v }))}
                availableTags={availableTags} darkMode={darkMode} t={t} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                <ColorCircle color={faultColor} onChange={setFaultColor} label="Fault" t={t} />
                <ColorCircle color={interColor} onChange={setInterColor} label="Intermediate" t={t} />
              </div>
              <div style={{ marginTop: '10px' }}>
                <TagRow label="Remote / Local" tagName={remoteLocal.tagName}
                  onTagChange={v => setRemoteLocal(p => ({ ...p, tagName: v }))}
                  availableTags={availableTags} darkMode={darkMode} t={t} showColor={false} />
                <div style={{ display: 'flex', gap: '8px', marginTop: '-4px' }}>
                  <ColorCircle color={remoteLocal.remoteColor} onChange={v => setRemoteLocal(p => ({ ...p, remoteColor: v }))} label="Remote" t={t} />
                  <ColorCircle color={remoteLocal.localColor} onChange={v => setRemoteLocal(p => ({ ...p, localColor: v }))} label="Local" t={t} />
                </div>
              </div>
            </div>

            {/* Faceplate */}
            <div style={{ background: t.card, borderRadius: '10px', padding: '14px', border: `1px solid ${t.border}` }}>
              <SectionHead icon={<Monitor size={12} color="#8b5cf6" />} title="Faceplate" t={t} />
              <Toggle value={showFaceplate} onChange={setShowFaceplate} label="Faceplate on Click" t={t} />
              <Toggle value={displayTrend} onChange={setDisplayTrend} label="Trend on Click" t={t} />
              <Toggle value={showStatusLabel} onChange={setShowStatusLabel} label="Status Label" t={t} />
            </div>
          </div>

          {/* Appearance row */}
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '10px', fontWeight: '600', color: t.sub, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Label</div>
              <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="Enter label…"
                style={{ width: '100%', padding: '8px 10px', background: t.card, border: `1px solid ${t.border}`, borderRadius: '8px', color: t.text, fontSize: '12px', boxSizing: 'border-box', outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingBottom: '2px' }}>
              <div style={{ fontSize: '10px', fontWeight: '600', color: t.sub, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Color</div>
              <div style={{ position: 'relative', width: '28px', height: '28px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: defaultColor, border: '2px solid rgba(255,255,255,0.1)', boxShadow: `0 0 8px ${defaultColor}44` }} />
                <input type="color" value={defaultColor} onChange={e => setDefaultColor(e.target.value)}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
              </div>
              <input type="text" value={defaultColor} onChange={e => setDefaultColor(e.target.value)}
                style={{ width: '72px', padding: '7px 8px', background: t.card, border: `1px solid ${t.border}`, borderRadius: '8px', color: t.text, fontSize: '11px', fontFamily: 'monospace', boxSizing: 'border-box', outline: 'none' }} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${t.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', background: 'transparent', border: `1px solid ${t.border}`, borderRadius: '8px', color: t.sub, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Cancel</button>
          <button onClick={handleSave} style={{ padding: '8px 24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: '600', boxShadow: '0 2px 8px rgba(102,126,234,0.4)' }}>Save</button>
        </div>
      </div>
    </div>
  );
}
